import Image from 'next/image';
import { useRouter } from 'next/router';
import {
  Text,
  Tag,
  Box,
  Stack,
  Button,
  Icon,
  useToast,
  Flex,
} from '@pairy/pds-react';
import { useState } from 'react';
import copy from 'copy-to-clipboard';
import { PdfThumbImage } from '@pairy/next-pdf-viewer';

import useFile from '@/hooks/queries/file/useFile';
import { getUTCDate } from '@/utils/getUTCDate';
import NotFoundErrorModal from '@/components/User/_components/common/NotFoundErrorModal';
import useShareUrl from '@/hooks/useShareUrl';

import FileRelatedButtons from '../FileRelatedButtons';
import EmailThumbnailModal from '../EmailThumbnailModal';
import ComponentToolTip from '../../common/Tags/ComponentToolTip';
import EmailCampaignSendButton from '../../common/EmailCampaignSendButton';

import OneToOneLinkButton from './OneToOneLinkGenerateButton';

const BulkPdfInfoWithButtons = () => {
  const router = useRouter();
  const toast = useToast();

  const fileId = Number(router.query.fileId);

  const [isNotFoundModalOpen, setIsNotFoundModalOpen] = useState(false);
  const [isEmailThumbnailModalOpen, setIsEmailThumbnailModalOpen] =
    useState(false);

  const { data } = useFile(
    {
      fileId,
    },
    {
      enabled: Boolean(fileId),
      select: (data) => data.data.data,
      onError: (error) => {
        if (error?.response?.status === 404) {
          setIsNotFoundModalOpen(true);
        }
      },
      retry: 1,
    },
  );

  const { url: shareUrl } = useShareUrl({
    targetType: 'BULK_FILE',
    identifier: data?.file.hashId ?? '',
  });

  const handleBulkViewerUrlCopy = () => {
    if (data?.file.hashId) {
      copy(shareUrl);
      toast('링크가 복사되었습니다.');
      return;
    }
    toast.error(
      `공유 링크를 복사하는데 실패했습니다.\n새로고침 후 다시 시도해주세요.`,
    );
  };

  const handleOpenQrView = () => {
    if (!data?.file.hashId) {
      toast.error(
        `공유 링크가 아직 생성되지 않았습니다.\n새로고침 후 다시 시도해주세요.`,
      );
      return;
    }
    window.open(
      `/user/qr?type=file&id=${fileId}`,
      '_blank',
      'noreferrer noopener',
    );
  };

  if (!data?.file) {
    return <div />;
  }

  return (
    <>
      <Box
        isTitleImage
        isMultipleCTAButtons
        title={
          data.file.imageSources ? (
            <PdfThumbImage
              thumb={data.file.imageSources.thumb}
              alt={data.file.name}
              fill
              objectFit="cover"
            />
          ) : (
            <Image src={data.file.thumnail} layout="fill" objectFit="cover" />
          )
        }
        bottomCTAButton={
          <Stack gap={8} direction="column">
            <FileRelatedButtons
              fileId={fileId}
              fileName={data.file.name}
              fileThumbnail={data.file.thumnail}
            />

            <Stack direction="column" gap={8} width="100%">
              <ComponentToolTip
                description={
                  <>
                    홈페이지·SNS 등 누구나 접근할 수 있는 곳에 공유할 때 <br />
                    사용하세요. 문의 양식을 제출하기 전 까지 익명으로
                    집계됩니다.
                  </>
                }
              >
                <Button
                  variant="black_to_zinc_900"
                  leftIcon={<Icon icon="ph:link" size={17} color="inherit" />}
                  radius={8}
                  paddingX={22}
                  paddingY={12}
                  textVariant="md-medium"
                  onClick={handleBulkViewerUrlCopy}
                  width="100%"
                  className={'share-link-button'}
                >
                  공유 링크 복사하기
                </Button>
              </ComponentToolTip>

              <ComponentToolTip
                description={
                  <>
                    행사장·부스 모니터나 노트북 화면에 QR 코드를 띄워두고,
                    <br />
                    방문자가 스캔해 바로 자료를 확인할 수 있습니다. QR로 들어온
                    <br />
                    열람은 UTM 캠페인 성과에서 구분됩니다.
                  </>
                }
              >
                <Button
                  variant="skyblue_400"
                  radius={8}
                  paddingX={16}
                  paddingY={12}
                  textVariant="md-medium"
                  leftIcon={
                    <Icon icon="ph:qr-code" size={17} color="inherit" />
                  }
                  onClick={handleOpenQrView}
                  width="100%"
                >
                  QR 코드 보기
                </Button>
              </ComponentToolTip>

              <Button
                variant="skyblue_400"
                radius={8}
                paddingX={16}
                paddingY={12}
                textVariant="md-medium"
                leftIcon={
                  <Icon icon="ph:image-square" size={17} color="inherit" />
                }
                onClick={() => setIsEmailThumbnailModalOpen(true)}
              >
                이메일에 붙여넣기
              </Button>

              <OneToOneLinkButton fileId={fileId} />
              <EmailCampaignSendButton targetType="FILE" targetId={fileId} />
              <Stack
                gap={4}
                alignItems="flex-start"
                justifyContent={'flex-start'}
                direction="row"
              >
                <Flex width={'fit-content'}>
                  <Icon icon="ph:info" size={16} color="zinc_400" />
                </Flex>
                <Text variant="sm-medium" color="zinc_400">
                  홈페이지·SNS 게시는 <b>공유 링크</b>,{'\n'}
                  이메일 등 특정 고객에게 보낼 땐 <b>1:1 공유용 링크</b>를
                  사용하세요.
                </Text>
              </Stack>
            </Stack>
          </Stack>
        }
      >
        <Text as="p" variant="lg-semibold">
          {data.file.name}
        </Text>
        <Text as="p" variant="sm-medium" color="zinc_400" marginTop={4}>
          {data.file.editedAt
            ? `${getUTCDate(data.file.editedAt, 'yearMonthDay')} 수정됨`
            : `${getUTCDate(data.file.createdAt, 'yearMonthDay')} 생성됨`}
        </Text>
        <Tag variant="loyalblue_50" marginTop={8} textVariant="md-medium">
          대량발송용
        </Tag>
      </Box>
      <NotFoundErrorModal
        isModalOpen={isNotFoundModalOpen}
        setModalOpen={setIsNotFoundModalOpen}
      />
      <EmailThumbnailModal
        isModalOpen={isEmailThumbnailModalOpen}
        setIsModalOpen={setIsEmailThumbnailModalOpen}
        thumbnailImgUrl={data.file.thumnail}
        title={data.file.name}
        link={shareUrl}
        totalCount={data.file?.pages ?? 0}
      />
    </>
  );
};

export default BulkPdfInfoWithButtons;
